import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';

import * as aqlActions from './aql-db.actions';
import {
  getAllSaveEvents,
  getAqlLockDetailsAndNextCacheResetTimestamp,
  getCompletedEventsAcrossAllLines,
  getCurrentWeekFailedEventsData,
  getRecentCategoryList,
  getSavedAuditData,
} from './aql-db.query';
import {
  TAQLAudit,
  TAqlAuditCycleMetaData,
  TAQLAuditMetaData,
  TAqlAuditRowWithSamples,
  TAqlEvent,
  TAqlEventWithoutStatus,
  TAqlSingleEvent,
  TAQLStore,
  TAqlStoreLockedBy,
  TAqlStoreLockResult,
  TBctxItemWithoutSize,
  TMasterdataItemInfoWithImages,
  TMeasurement_issue,
  TSingleAuditDetail,
  TAqlListingPagesType,
  TREQUEST_AuditLogsDetails,
  TAql_level,
  TAQLAuditStatusTypes,
  TAqlAuditRowWithIssuesAndPOMWithSamples,
  TListingPagesTypeWithData,
  TBCTXKeysWithoutSize,
  TActionTaken,
  TRESPONSE_AuditSummary,
  TREQUEST_AuditSummary,
  TREQUEST_AuditSummaryFiltersData,
  TAqlLineMap,
} from '@monorepo/types';
import { AqlUtil, BCTXComputationHelper, InputAppAPIHelperUtil } from '@monorepo/utils';
import { AqlAppDataService, CustomLoggerService } from '@monorepo/services';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/internal/Observable';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import produce from 'immer';
import {
  AcquireLockAndThenInitAqlStore,
  AquireLockAndDeleteAudit,
  AquireLockAndUpdateWeeklyfailedEvents,
  AquireLockSaveAndUpdateCompletedEventsAndrecentCateGoryList,
  AquireLockUpdateSavedEventsAndrecentCateGoryList,
  UpdateAqlStoreLockDetails,
} from './aql-db.actions';
import { InputAppClientDBFacade } from '../input-app-client-db/input-app-client-db.facade';
import { from, of, Subject, lastValueFrom, finalize } from 'rxjs';
import { AppMasterDBFacade } from '../app-master-db/app-master-db.facade';
import { getBctxItemsOrderWithoutSize, getDepartment } from '../app-master-db/app-master.query';
import moment from 'moment';
import { TBucketId } from 'libs/services/src/lib/capacitor/bucket-storage.types';
import { InputAppImageDBFacade } from '@monorepo/core-data';

@Injectable({
  providedIn: 'root',
})
export class AqlStoreFacade {
  private readonly COMPONENT_NAME = 'AqlStoreFacade';
  private listingPagesSubscription = new Subject<TListingPagesTypeWithData>();
  private auditSummarySubscription = new Subject<TRESPONSE_AuditSummary>();
  private saveAndExitSubscription = new Subject();

  constructor(
    private store: Store<TAQLStore>,
    private inputAppAPIHelperUtil: InputAppAPIHelperUtil,
    private aqlAppDataService: AqlAppDataService,
    private translateService: TranslateService,
    private inputAppClientDbFacade: InputAppClientDBFacade,
    private appMasterDBFacade: AppMasterDBFacade,
    private customLoggerService: CustomLoggerService,
    private inputAppImageDBFacade: InputAppImageDBFacade,
  ) {}

  getListingPagesSubscription() {
    return this.listingPagesSubscription.asObservable();
  }

  emitListingPagesSubscription(res: TListingPagesTypeWithData) {
    this.listingPagesSubscription.next(res);
  }

  getAuditSummarySubscription() {
    return this.auditSummarySubscription.asObservable();
  }

  emitAuditSummarySubscription(res: TRESPONSE_AuditSummary) {
    this.auditSummarySubscription.next(res);
  }

  getSaveAndExitSubscription() {
    return this.saveAndExitSubscription;
  }

  emitSaveAndExitSubscription() {
    this.saveAndExitSubscription.next(1);
  }

  fetchAuditSummaryPageData(
    payload: TREQUEST_AuditSummary | TREQUEST_AuditSummaryFiltersData,
    showLoading: boolean = false,
  ) {
    showLoading && this.inputAppAPIHelperUtil.presentLoading('Fetching Data');
    return lastValueFrom(this.aqlAppDataService.getAqlAuditSummary(payload)).finally(() => {
      showLoading && this.inputAppAPIHelperUtil.dismissLoading();
    });
  }

  initStore(initBy: TAqlStoreLockedBy) {
    this.store.dispatch(new AcquireLockAndThenInitAqlStore(initBy));
  }

  acquireLock(lockFor: TAqlStoreLockedBy): Observable<TAqlStoreLockResult> {
    return this.getRoamingQcLockDetailsAndNextCacheResetTimestamp().pipe(
      take(1),
      map(({ lockDetails, nextCacheResetTimestamp }) => {
        if (lockDetails.isLocked) {
          this.customLoggerService.log('INPUT_APP_AQL_DB_LOCK_ACQUIRE_FAILURE_ALREADY_LOCKED', {
            lockFor,
            nextCacheResetTimestamp,
            currentlyLockedBy: lockDetails.lockedBy,
          });
          return {
            status: 'FAILURE',
            lockedBy: lockDetails.lockedBy,
            message: `${this.translateService.instant('roaming_qc_common.please_wait')} (${lockDetails.lockedBy})`,
          };
        } else if (lockFor !== 'INIT_PENDING' && nextCacheResetTimestamp < new Date().getTime()) {
          this.customLoggerService.log('INPUT_APP_AQL_DB_LOCK_ACQUIRE_FAILURE_FACTORY_END_TIME', {
            lockFor,
            nextCacheResetTimestamp,
          });
          return {
            status: 'FAILURE',
            lockedBy: lockDetails.lockedBy,
            message: this.translateService.instant('roaming_qc_common.factoryEndTimeWaitTillReset'),
          };
        } else {
          this.store.dispatch(
            new UpdateAqlStoreLockDetails(
              produce(lockDetails, (draft) => {
                draft.isLocked = true;
                draft.lockedBy = lockFor;
              }),
            ),
          );
          return {
            status: 'SUCCESS',
            lockedBy: lockFor,
          };
        }
      }),
    );
  }

  getRoamingQcLockDetailsAndNextCacheResetTimestamp() {
    return this.store.pipe(select(getAqlLockDetailsAndNextCacheResetTimestamp));
  }

  async handleDefectsImageUploadSync(bucketIds: TBucketId[]): Promise<{ status: 'SUCCESS' | 'FAILURE' }> {
    if (bucketIds.length === 0) {
      return { status: 'SUCCESS' };
    }
    this.inputAppAPIHelperUtil.presentLoading('Uploading defect images, please wait...');
    try {
      const result = await lastValueFrom(
        this.handleUploadImages(bucketIds).pipe(
          take(1),
          finalize(() => {
            this.inputAppAPIHelperUtil.dismissLoading();
          }),
        ),
      );
      return result;
    } catch (error) {
      this.inputAppAPIHelperUtil.dismissLoading();
      this.customLoggerService.log(`[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error in handleDefectsImageUploadSync`, {
        bucketIds: bucketIds,
        error: JSON.stringify(error),
      });
      return { status: 'FAILURE' };
    }
  }

  handleUploadImages(bucketIds: TBucketId[]): Observable<{
    status: 'SUCCESS' | 'FAILURE';
  }> {
    return from(this.inputAppImageDBFacade.uploadAllBuckets(bucketIds)).pipe(
      map((response) => {
        const allImagesUploadedSuccessfully = Object.values(response).every((v) => v === 'SYNCED');
        if (allImagesUploadedSuccessfully) {
          return { status: 'SUCCESS' as const };
        } else {
          const failedImages = Object.entries(response)
            .filter(([_, status]) => status !== 'SYNCED')
            .map(([id, status]) => ({ id, status }));
          this.customLoggerService.log(`[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Some images failed to upload`, {
            bucketIds: bucketIds,
            failedImages: failedImages,
          });
          return { status: 'FAILURE' as const };
        }
      }),
      catchError((error) => {
        this.customLoggerService.log(`[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error uploading bucket IDs to S3`, {
          bucketIds: bucketIds,
          message: error?.message,
        });
        return of({ status: 'FAILURE' as const });
      }),
    );
  }

  saveEvent(singleAuditDetail: TSingleAuditDetail, loaderMessage: string) {
    this.store.dispatch(new AquireLockSaveAndUpdateCompletedEventsAndrecentCateGoryList(singleAuditDetail, loaderMessage));
  }

  releaseLock() {
    this.store.dispatch(new UpdateAqlStoreLockDetails({ isLocked: false, lockedBy: 'NONE' }));
  }

  aquireLockUpdateSavedEventsAndrecentCateGoryList(bulkEvent: TAqlEvent) {
    this.store.dispatch(new AquireLockUpdateSavedEventsAndrecentCateGoryList(bulkEvent));
  }

  getBulkEvent(singleAuditDetail: TSingleAuditDetail): Observable<TAqlEvent> {
    return this.inputAppClientDbFacade.getAqlBulkEventsWithoutStatus(singleAuditDetail).pipe(
      take(1),
      switchMap((singEvent: TAqlEventWithoutStatus) => {
        const bulkEvent = this.getAqlBulkEventWithStatus(singEvent);
        return of(bulkEvent);
      }),
    );
  }

  getSingleAuditDetail(
    rows: TAqlAuditRowWithSamples[],
    bctxitems: TBctxItemWithoutSize[],
    cycleMetaData: TAqlAuditCycleMetaData,
    status: TAQLAuditStatusTypes,
    aql_level: TAql_level,
    type: TActionTaken,
  ): TSingleAuditDetail {
    let pass = 0;
    let fail = 0;
    let qty = 0;
    let sample_qty = 0;
    rows.forEach((row) => {
      pass = pass + row.row_pass;
      fail = fail + row.row_fail;
      (sample_qty = sample_qty + row.row_sample), (qty = qty + row.row_qty);
    });
    return {
      metaData: {
        ...cycleMetaData,
        action_taken: type,
        status,
        datetime: 0,
      },
      bctxItemListWithoutSize: bctxitems,
      summary: {
        pass,
        fail,
        qty,
        sample_qty,
      },
      rows: this.getAuditRowWithcombinedIssues(rows),
      aql_level,
    };
  }

  getSingleAuditDetailWithAqlAudit(aqlAudit: TAQLAudit, type: TActionTaken): TSingleAuditDetail {
    const singleAudit: TSingleAuditDetail = {
      metaData: {
        auditCycle: aqlAudit.metaData.auditCycle,
        action_taken: type,
        auditId: aqlAudit.metaData.auditId,
        reAuditId: aqlAudit.metaData.reAuditId,
        auditDisplayName: aqlAudit.metaData.auditDisplayName,
        datetime: aqlAudit.metaData.datetime,
        status: aqlAudit.statusInfo.status,
      },
      bctxItemListWithoutSize: Object.entries(aqlAudit.auditDetails.sampleBctxDetails).map(([key, value]) => ({
        k: key as TBCTXKeysWithoutSize, // Type assertion to ensure the key is of the correct type
        v: value!,
      })),
      summary: {
        qty: aqlAudit.auditDetails.sampleQtyPassFailTotalData.qty,
        sample_qty: aqlAudit.auditDetails.sampleQtyPassFailTotalData.pass,
        pass: aqlAudit.auditDetails.sampleQtyPassFailTotalData.fail,
        fail: aqlAudit.auditDetails.sampleQtyPassFailTotalData.sample_qty,
      },
      aql_level: aqlAudit.auditDetails.aql_level,
      rows: aqlAudit.auditDetails.sizeBasedQtyPassFailData.map((item, index) => {
        let issues: TMasterdataItemInfoWithImages[] = [];
        let pass_measurement: TMeasurement_issue[] = [];
        let fail_measurement: TMeasurement_issue[] = [];
        item.samples &&
          item.samples.forEach((sample) => {
            issues = issues.concat(sample.issues);
            pass_measurement = pass_measurement.concat(sample.pass_measurement);
            fail_measurement = fail_measurement.concat(sample.fail_measurement);
          });
        return {
          issues,
          samples: item.samples,
          pass_measurement,
          fail_measurement,
          row_id: item.row_id,
          row_qty: item.row_total_qty,
          row_sample: item.row_sample_qty,
          row_pass: item.pass_count,
          row_fail: item.fail_count,
          size: item.size.name,
          index,
        };
      }),
    };

    return singleAudit;
  }

  getAuditRowWithcombinedIssues(rows: TAqlAuditRowWithSamples[]): TAqlAuditRowWithIssuesAndPOMWithSamples[] {
    return rows.map((row) => {
      let issues: TMasterdataItemInfoWithImages[] = [];
      let pass_measurement: TMeasurement_issue[] = [];
      let fail_measurement: TMeasurement_issue[] = [];
      row.samples.forEach((sample) => {
        issues = issues.concat(sample.issues);
        pass_measurement = pass_measurement.concat(sample.pass_measurement);
        fail_measurement = fail_measurement.concat(sample.fail_measurement);
      });

      return {
        pass_measurement,
        fail_measurement,
        issues,
        row_id: row.row_id,
        row_qty: row.row_qty,
        row_sample: row.row_sample,
        row_pass: row.row_pass,
        row_fail: row.row_fail,
        size: row.size,
        index: row.index,
        samples: row.samples.map((sample) => sample),
      };
    });
  }

  // IN this function  backgroundMetadata and eventBackgroundMetadata are hardcoded, because app is online, in case of offline we nned to change this
  getAqlBulkEventWithStatus(aqlEventWithoutStatus: TAqlEventWithoutStatus): TAqlEvent {
    return {
      backgroundMetadata: {
        remainingRetriesCount: 1,
        bulkEventStatus: 'YET_TO_BE_SYNCED',
        groupId: '',
      },
      metadata: aqlEventWithoutStatus.metadata,
      type: aqlEventWithoutStatus.type,
      events: aqlEventWithoutStatus.events.map((event) => {
        const aqlSingleEvent: TAqlSingleEvent = {
          eventBackgroundMetadata: {
            status: 'YET_TO_BE_SYNCED',
            eventId: 1,
            v_id: event.eventInfo.v_id,
          },
          eventInfo: {
            ...event.eventInfo,
          },
          type: event.type,
          actionType: event.actionType,
        };
        return aqlSingleEvent;
      }),
    };
  }

  getAqlLevelsBasedOnBuyer(buyer: string, factoryId: string, department: string) {
    return lastValueFrom(
      this.aqlAppDataService.getAqlLevel({ buyer: buyer, factoryId: factoryId, department: department }),
    );
  }

  fetchAuditListingInfo(
    payload: TREQUEST_AuditLogsDetails,
    listingType: TAqlListingPagesType,
    showLoader: boolean = true,
  ) {
    this.store.dispatch(new aqlActions.GetAuditListingMappedWithLocalStorage(payload, listingType, showLoader));
  }

  getCurrentWeekFailedEventsData() {
    return this.store.pipe(select(getCurrentWeekFailedEventsData));
  }

  getSavedAuditData() {
    return this.store.pipe(select(getSavedAuditData));
  }

  getBctxItemsOrderWithoutSize() {
    return this.store.pipe(select(getBctxItemsOrderWithoutSize));
  }

  getRecentCategoryList() {
    return this.store.pipe(select(getRecentCategoryList));
  }

  getRecentCategoryListByBctx(bctxItemListWithoutSize: TBctxItemWithoutSize[]) {
    return this.getBctxItemsOrderWithoutSize().pipe(
      take(1),
      switchMap((res) => {
        return this.getRecentCategoryList().pipe(
          take(1),
          map((recentList) => {
            const bctxKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(res, bctxItemListWithoutSize);
            return recentList[bctxKey] ? recentList[bctxKey] : [];
          }),
        );
      }),
    );
  }

  aquireLockAndDeleteAudit(aqlAuditMetaData: TAQLAuditMetaData) {
    this.store.dispatch(new AquireLockAndDeleteAudit(aqlAuditMetaData));
  }

  isAqlDbLocked() {
    return this.store.pipe(select(getAqlLockDetailsAndNextCacheResetTimestamp)).pipe(
      take(1),
      map(({ lockDetails }) => !lockDetails.isLocked),
    );
  }

  getCompletedEventsAcrossAllLines() {
    return this.store.pipe(select(getCompletedEventsAcrossAllLines));
  }

  fetchWeeklyFailedEvents() {
    this.appMasterDBFacade
      .getAllStations()
      .pipe(
        take(1),
        switchMap((allStations) => {
          return this.appMasterDBFacade.getSubjectKey().pipe(
            take(1),
            map((factoryId) => {
              return [factoryId, allStations] as const;
            }),
          );
        }),
        switchMap(([factoryId, allStations]) => {
          const currentTime = moment().valueOf();
          const currentWeekStartAndEndTime = {
            start: moment(currentTime).subtract(6, 'days').valueOf(),
            end: currentTime,
          };
          return from(
            this.aqlAppDataService.getAqlListingPagesLogs(
              AqlUtil.createAqlListingLogsPayload('REAUDIT', {
                factoryId: factoryId,
                allStations: allStations ? allStations : [],
                startAndEndTime: currentWeekStartAndEndTime,
              }),
            ),
          );
        }),
      )
      .subscribe(
        (res) => {
          if (res && res.hbase_logs_query_request && res.hbase_logs_query_request.data) {
            this.store.dispatch(new AquireLockAndUpdateWeeklyfailedEvents(res.hbase_logs_query_request.data));
          }
        },
        (error) => {
          this.customLoggerService.log(
            `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error fetching weekly failed events`,
            JSON.stringify(error),
          );
        },
      );
  }

  getAllSaveEvents() {
    return this.store.pipe(select(getAllSaveEvents));
  }

  getCompletedEventsForSelectedLineAndShift() {
    return this.getCompletedEventsAcrossAllLines().pipe(
      take(1),
      switchMap((completedEvents) => {
        return this.filterLineMapEventsBasedONSelectedStation(completedEvents);
      }),
    );
  }

  getSavedEventsForSelectedLineAndShift() {
    return this.getAllSaveEvents().pipe(
      switchMap((savedEvents) => {
        return this.filterLineMapEventsBasedONSelectedStation(savedEvents);
      }),
    );
  }

  filterLineMapEventsBasedONSelectedStation(events: TAqlLineMap) {
    return this.inputAppClientDbFacade.getSelectedStationAndShiftDTO().pipe(
      map((selectedStation) => {
        const selectedLine = selectedStation.subject.subject;
        const selectedShift = selectedStation.shift.id;
        if (
          selectedLine &&
          selectedShift &&
          events.lineMap[selectedLine] &&
          events.lineMap[selectedLine].shiftMap[selectedShift]
        ) {
          return events.lineMap[selectedLine].shiftMap[selectedShift];
        }
        return [];
      }),
    );
  }

  getCurrentWeeklyFailedEventsForSelectedLine() {
    return this.inputAppClientDbFacade.getSelectedStationAndShiftDTO().pipe(
      switchMap((selectedStation) => {
        return this.getCurrentWeekFailedEventsData().pipe(
          map((completedEvents) => {
            const selectedLine = selectedStation.subject;
            return completedEvents.filter((event) => event.metaData.lineAndHskDetails.dnm === selectedLine.dis_name);
          }),
        );
      }),
    );
  }

  getDepartment() {
    return this.store.pipe(select(getDepartment));
  }
}
